import { useState, useEffect, useCallback } from 'react';
import { notificationsService } from '@/services/notifications.service';
import { useAuth } from '@/contexts/AuthContext';
import supabase from '@/supabase/supabase';

// Global channel registry to prevent duplicate subscriptions
const activeChannels = new Map();

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [notifResult, countResult] = await Promise.all([
        notificationsService.getByUser(user.id),
        notificationsService.getUnreadCount(user.id),
      ]);
      setNotifications(notifResult.data || []);
      setUnreadCount(countResult.count || 0);
    } catch (err) {
      console.error('Notifications fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    if (!user?.id) return;

    const channelKey = `notifs:${user.id}`;

    // If a channel already exists for this user, reuse it
    if (activeChannels.has(channelKey)) {
      const existingChannel = activeChannels.get(channelKey);
      
      // Subscribe to updates from the existing channel
      const handleUpdate = (payload) => {
        setNotifications((prev) => [payload.new, ...prev]);
        setUnreadCount((prev) => prev + 1);
      };

      // Store the handler so we can clean it up later
      existingChannel.handlers = existingChannel.handlers || [];
      existingChannel.handlers.push(handleUpdate);

      return () => {
        // Remove this specific handler
        if (existingChannel.handlers) {
          const index = existingChannel.handlers.indexOf(handleUpdate);
          if (index > -1) {
            existingChannel.handlers.splice(index, 1);
          }
        }
      };
    }

    // Create a new channel only if one doesn't exist
    const channel = supabase
      .channel(channelKey)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // Broadcast to all handlers
          if (channel.handlers) {
            channel.handlers.forEach(handler => handler(payload));
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`Notifications channel subscribed for user ${user.id}`);
        }
      });

    // Initialize handlers array
    channel.handlers = [];

    // Add the first handler
    const handleUpdate = (payload) => {
      setNotifications((prev) => [payload.new, ...prev]);
      setUnreadCount((prev) => prev + 1);
    };
    channel.handlers.push(handleUpdate);

    // Store in global registry
    activeChannels.set(channelKey, channel);

    return () => {
      // Remove this handler
      if (channel.handlers) {
        const index = channel.handlers.indexOf(handleUpdate);
        if (index > -1) {
          channel.handlers.splice(index, 1);
        }
      }

      // Only remove the channel if no more handlers are using it
      if (channel.handlers && channel.handlers.length === 0) {
        activeChannels.delete(channelKey);
        supabase.removeChannel(channel);
        console.log(`Notifications channel unsubscribed for user ${user.id}`);
      }
    };
  }, [user?.id]);

  const markAsRead = async (id) => {
    try {
      await notificationsService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Mark as read error:', err);
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id) return;
    try {
      await notificationsService.markAllAsRead(user.id);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Mark all as read error:', err);
    }
  };

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetch,
  };
}